import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  DrawRequestPreviewInterface,
  DrawRequestResp,
  PaymentPreviewInterface,
  PaymentForm,
  DrawRequestMemoInterface
} from "../../../types/draw-request";
import { drawRequestsService } from "../../../services/draw-request";
import { Button, Col, message, Popconfirm, Popover, Row, Spin, Table, TableColumnsType } from "antd";
import { DrawRequestPreview } from "../components/DrawRequestPreview/DrawRequestPreview";
import { ArrowLeftOutlined, CheckOutlined, CloseOutlined, StopOutlined } from "@ant-design/icons";
import { generateDownload, turnDate } from "../../../helpers";
import moment from "moment";
import { DataContext } from "../../../contexts/DataContext";
import { AuthContext } from "../../../contexts/AuthContext";
import { DenyDrawRequestModal } from "../components/DenyDrawRequestModal/DenyDrawRequestModal";
import { SignatureModal } from "../../../components/SignatureModal/SignatureModal";
import { canBeApprovedOrDenied } from "../helpers/canBeApprovedOrDenied";
import { canVoid } from "../helpers/canVoid";

interface Props {}

/**========================================================================
 *                          external const here
 *========================================================================**/

/*============================ END OF SECTION ============================*/

/**========================================================================
 *                           external methods here
 *========================================================================**/

/*============================ END OF SECTION ============================*/

export const PreviewApproveDenyDrawRequest: FC<Props> = () => {
  /**========================================================================
   *                           Hooks here
   *========================================================================**/
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    projects,
    signatureContext: { setSignature, signature }
  } = useContext(DataContext);
  const { user, isPayment, isTreasury, userExtraInfo } = useContext(AuthContext);

  useEffect(() => {
    if (id) setValues(id);
  }, [id]);

  const [drawRequestPreview, setDrawRequestPreview] = useState<DrawRequestPreviewInterface | undefined>(undefined);
  const [paymentPreview, setPaymentPreview] = useState<PaymentPreviewInterface | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [denyModal, setDenyModal] = useState<boolean>(false);
  const [signatureModal, setSignatureModal] = useState<boolean>(false);
  const [drawRequestState, setDrawRequestState] = useState<DrawRequestResp | undefined>(undefined);
  /*============================ END OF SECTION ============================*/

  /**========================================================================
   *                           Const here
   *========================================================================**/

  /*============================ END OF SECTION ============================*/

  /**========================================================================
   *                           Methods here
   *========================================================================**/
  const setValues = async (id: string) => {
    try {
      setIsLoading(true);
      const drawRequest: DrawRequestResp = await drawRequestsService.getDrawRequestById(id);

      if (!drawRequest) throw new Error("not draw request found");

      setDrawRequestState(drawRequest);

      setDrawRequestPreview({
        amount: drawRequest.amount,
        drawInvoice: drawRequest.drawNumber,
        dueDate: turnDate(moment(drawRequest.dueDate)),
        invoiceDate: turnDate(moment(drawRequest.invoiceDate)),
        initiator: drawRequest.initiator,
        paymentMethod: drawRequest.paymentMethod?.id || 1,
        Project: projects.find((project) => project.id === drawRequest.projectID)?.name || "",
        glCode: drawRequest.mainCmsGlCodeID,
        jvDeposit: drawRequest.jvDepositGlCodeID,
        jvWithdrawal: drawRequest.jvWithdrawalGlCodeID,
        memos: drawRequest.actionMemos || [],
        controlNumber: drawRequest.controlNumber
      });

      setPaymentPreview({
        sanctionNo: drawRequest?.paymentForm?.sanctionNo || "",
        amountBreakdowns:
          drawRequest?.amountBreakdowns?.map((am) => ({
            amount: am.amount,
            salesTax: am.salesTax,
            glCodeID: am.glCodeID,
            name: am.name,
            relatedLocation: am.relatedLocation
          })) || [],
        payee: drawRequest?.paymentForm?.payee || "",
        vendorCode: drawRequest?.paymentForm?.vendorCode,
        // relatedLocation: drawRequest?.paymentForm?.relatedLocation,
        address: drawRequest?.paymentForm?.address,
        address2: drawRequest?.paymentForm?.address2,
        chargeTo: drawRequest?.paymentForm?.chargeTo,
        specify: drawRequest?.paymentForm?.specify || "",
        abbaOrSwiftAccountNo: drawRequest?.paymentForm?.abbaOrSwiftAccountNo,
        accountNo: drawRequest?.paymentForm?.accountNo,
        approvers: drawRequest?.approvers?.filter((ap) => ap.role === "approver") || [],
        reviewers: drawRequest?.approvers?.filter((ap) => ap.role === "reviewer") || [],
        expatInChargeId: drawRequest?.paymentForm?.expatInChargeId,
        expatOfficerId: drawRequest?.paymentForm?.expatOfficerId,
        documents: drawRequest.documents || [],
        agreementPath: drawRequest?.paymentForm?.agreementPath || "",
        debitBlockID: drawRequest?.paymentForm?.debitBlockID || "",
        checkSpecialInstructions: drawRequest?.paymentForm?.checkSpecialInstructions || "",
        bankName: drawRequest?.paymentForm?.bankName || ""
      });
    } catch (error) {
      console.error(error);
      message.error("Ups: something was wrong previewing the draw request, please try again later or contact the support");
      navigate("/draw-requests");
    }
    setIsLoading(false);
  };

  const onSubmitSignature = (signature: string) => {
    setSignature(signature);
    approve(signature, id as string);
  };

  const approve = async (signature: string, id: string | number) => {
    setIsLoading(true);
    try {
      await drawRequestsService.approveDrawRequest(id, signature);
      message.success(`Draw request approved`);
      navigate("/draw-requests");
    } catch (error) {
      console.error(error);
      message.error(`Ups: something was wrong approving the draw request, please try again later or contact the support team`);
    }

    setIsLoading(false);
  };

  const areYouNextToApprove = () => {
    // return 1;
    const approvers = drawRequestState?.approvers || [];
    const isThere = approvers?.find((ap) => ap.email === user?.username);

    if (!isThere)
      //user is not in the approver list -3
      return -3;
    if (isThere?.hasApproved !== null)
      // user already approve -1 or denied -2 the draw request
      return isThere?.hasApproved ? -1 : -2;

    const next = approvers?.find((app) => app?.hasApproved === null);
    if (!next)
      //draw request full approved or denied
      return 0;

    return user?.username === next?.email ? 1 : 0;
  };

  const approversTSX = () => {
    const status = drawRequestState?.approvalStatus || "Voided";

    if (["voided", "rejected", "completed"].includes(status.toLocaleLowerCase())) return <></>;

    const value = areYouNextToApprove();

    if (value !== 1) return <></>;

    return (
      <Row style={{ marginBottom: "28px", display: "flex", justifyContent: "flex-end" }}>
        <Popover
        // visible={value !== 1 ? undefined : false}
        // content={
        //   <>
        //     {value === -3 && <p>You are not an approver</p>}
        //     {value === -2 && <p>You already denied this request</p>}
        //     {value === 0 && <p>You need to wait for the rest approvers to approve this request</p>}
        //   </>
        // }
        >
          <Col>
            <Popconfirm
              title="Are you sure you want to approve this Draw Request?"
              disabled={value !== 1}
              onConfirm={() => {
                setSignatureModal(true);
              }}
            >
              <Button disabled={value !== 1} type="primary" style={{ marginRight: "10px" }}>
                Approve
              </Button>
            </Popconfirm>

            <Popconfirm
              title="Are you sure you want to decline this Draw Request?"
              disabled={value !== 1}
              onConfirm={() => {
                setDenyModal(true);
              }}
            >
              <Button disabled={value !== 1} type="primary" danger>
                Decline
              </Button>
            </Popconfirm>
          </Col>
        </Popover>
      </Row>
    );
  };

  const GetAllPDFsByDRid = async () => {
    setIsLoading(true);
    try {
      const resp = await drawRequestsService.GetAllPDFsByDRid(Number(id));

      if (resp.status === 204) message.info("Not documents found");
      else {
        const file = resp.data;

        const fileName = `PR - ${drawRequestPreview?.controlNumber || "unknown-controlNumber"} - ${paymentPreview?.payee || "unknown-payee"} - ${drawRequestPreview?.dueDate || "unknown-dueDate"}`;

        // const fileName = `draw-request-documents ${new Date().toUTCString()}`;

        generateDownload(file, fileName, "pdf");

        message.info("All Document downloaded");
      }
    } catch (error) {
      console.error(error);
      message.error("Ups: something was wrong downloading the documents, please try again later or contact the support");
    }
    setIsLoading(false);
  };

  // const cannotBeModify = () => {
  //   return (
  //     drawRequestState?.accountingApprovalStatus?.toLowerCase() === "approve" &&
  //     drawRequestState?.approvalStatus?.toLowerCase() === "complete"
  //   );
  // };

  const approveDenyTransaction = async (isApprove: boolean = true) => {
    setIsLoading(true);
    try {
      const drawRequest = await drawRequestsService.approveDenyTransaction(id!, isApprove);
      setDrawRequestState(drawRequest);

      message.success(`transaction ${isApprove ? "approved" : "denied"}`);
    } catch (error) {
      console.error(error);
      message.error(
        `Ups: something was wrong ${
          isApprove ? "approving" : "denying"
        } the transaction, please try again later or contact the support team`
      );
    }
    setIsLoading(false);
  };

  const columns: TableColumnsType<DrawRequestMemoInterface> = useMemo(
    () => [
      {
        dataIndex: "columnName",
        title: "Column Name",
        key: "columnName",
        width: "10%",
        align: "center"
      },
      {
        dataIndex: "newValue",
        title: "New Value",
        key: "newValue",
        width: "10%",
        render: (text: string) => JSON.stringify(text)
      },
      {
        dataIndex: "changedAt",
        title: "Change Date",
        key: "changedAt",
        width: "10%",
        render: (text: string) => (text ? moment(text).format("MM/DD/YYYY") : "none")
      },
      {
        dataIndex: "changedBy",
        title: "Change By",
        key: "changedBy",
        width: "10%"
      }
    ],
    [drawRequestPreview]
  );

  const voidDrawRequest = async () => {
    try {
      if (!drawRequestState) return false;
      setIsLoading(true);
      const resp = await drawRequestsService.voidDrawRequest(drawRequestState.id);
      setDrawRequestState(resp);
      message.success(`Draw request voided`);
    } catch (error) {
      console.error(error);
      message.error(`Ups: something was wrong voiding the draw request, please try again later or contact the support team`);
    }
    setIsLoading(false);
  };

  const canVoidAux = useCallback(() => {
    return canVoid(drawRequestState, userExtraInfo);
  }, [drawRequestState, userExtraInfo]);

  // const deleteDrawRequest = async () => {
  //   try {
  //     setIsLoading(true);
  //     await drawRequestsService.deleteDrawRequest(Number(id) as number);
  //     message.success(`Draw request rejected`);
  //     navigate("/draw-requests");
  //   } catch (error) {
  //     console.error(error);
  //     message.error(`Ups: something was wrong rejecting the draw request, please try again later or contact the support team`);
  //   }
  //   setIsLoading(false);
  // };
  /*============================ END OF SECTION ============================*/

  return (
    <>
      {/* /========================================================================  HTML here
    ========================================================================*/}
      <Spin spinning={isLoading} tip="Downloading documents ..." size="large">
        <Row style={{ marginBottom: "10px" }}>
          <Col xs={24} md={20} style={{ display: "flex", alignItems: "center" }}>
            <Button
              style={{
                border: "none",
                boxShadow: "none"
              }}
              onClick={() => {
                // navigate("/draw-requests");
                navigate(-1);
              }}
            >
              <ArrowLeftOutlined />
            </Button>

            <h2 style={{ margin: 0 }}>Review Payment Request</h2>
          </Col>
          <Col xs={24} md={4} style={{ display: "flex", justifyContent: "flex-end" }}>
            {/* <Popover
              visible={drawRequestState?.isDraft ? undefined : false}
              content={<>This is just a draft, please finish it in order to download the documents</>}
            > */}
            <Button
              // disabled={drawRequestState?.isDraft}
              type="primary"
              onClick={() => GetAllPDFsByDRid()}
            >
              Download Documents
            </Button>
            {/* </Popover> */}
          </Col>
        </Row>
        <hr />
        {!drawRequestState?.isDraft && (
          <>
            <Row style={{ marginBottom: "28px", display: "flex", justifyContent: "flex-end" }}>
              <div>
                {canBeApprovedOrDenied(drawRequestState, isPayment, isTreasury) && (
                  <>
                    <Popconfirm
                      title="Are you sure you want to approve this transaction?"
                      onConfirm={() => {
                        approveDenyTransaction(true);
                      }}
                    >
                      <Button style={{ padding: "5px", marginRight: 5 }} type="primary">
                        <CheckOutlined style={{ fontSize: "1.2rem" }} />
                      </Button>
                    </Popconfirm>

                    <Popconfirm
                      title="Are you sure you want to deny this transaction?"
                      onConfirm={() => {
                        approveDenyTransaction(false);
                      }}
                    >
                      <Button style={{ padding: "5px", marginRight: 5 }} type="primary" danger>
                        <CloseOutlined style={{ fontSize: "1.2rem" }} />
                      </Button>
                    </Popconfirm>
                  </>
                )}
                {canVoidAux() && (
                  <Popconfirm
                    title="Are you sure you want to void this draw request?"
                    onConfirm={() => {
                      voidDrawRequest();
                    }}
                  >
                    <Button style={{ padding: "5px", marginRight: 5 }} type="primary" danger>
                      <StopOutlined style={{ fontSize: "1.2rem" }} />
                    </Button>
                  </Popconfirm>
                )}

                {/* {(isTreasury || isAccountant) && (
                  <Popconfirm
                    title="Are you sure you want to reject this draw request?"
                    onConfirm={() => {
                      deleteDrawRequest();
                    }}
                    disabled={cannotBeModify()}
                  >
                    <Button disabled={cannotBeModify()} style={{ padding: "5px", marginRight: 5 }} type="primary" danger>
                      <StopOutlined style={{ fontSize: "1.2rem" }} />
                    </Button>
                  </Popconfirm>
                )} */}
              </div>
            </Row>
            {approversTSX()}
          </>
        )}
        {!!drawRequestState?.isDraft && (
          <Row style={{ padding: 10, fontWeight: 800, fontSize: 18 }}>
            <div className="fc-red">(*Draft)</div>
          </Row>
        )}
        {!isLoading && (
          <>
            <Row>
              <Col span={24}>
                <DrawRequestPreview
                  drawRequestPreview={drawRequestPreview}
                  paymentPreview={paymentPreview}
                  setIsLoading={setIsLoading}
                ></DrawRequestPreview>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Row>
                  <h2>Memos:</h2>
                </Row>
                <Table
                  rowKey="id"
                  sticky
                  dataSource={drawRequestPreview?.memos || []}
                  columns={columns}
                  pagination={false}
                  bordered
                  scroll={{ x: 1000 }}
                />
              </Col>
            </Row>
          </>
        )}
      </Spin>

      <DenyDrawRequestModal
        onDeny={() => {
          navigate("/draw-requests");
        }}
        drawRequestId={id}
        visible={denyModal}
        setVisible={setDenyModal}
      ></DenyDrawRequestModal>

      <SignatureModal
        currentSignature={signature && signature != "data:image/png;base64," ? signature : ""}
        setSignature={setSignature}
        visible={signatureModal}
        setVisible={setSignatureModal}
        onSubmitSignature={onSubmitSignature}
      ></SignatureModal>
      {/*============================ END OF SECTION ============================*/}
    </>
  );
};
